import { type ApolloError, useMutation, useSuspenseQuery } from '@apollo/client'
import { repackReportKey } from '@faceup/crypto'
import { useCryptoErrorHandler } from '@faceup/report'
import { useNavigate, useSearchParams } from '@faceup/router'
import { withModalSuspense } from '@faceup/ui'
import { ModalConfirm, useMessage } from '@faceup/ui-base'
import { graphql } from '../__generated__'

const query = {
  GrantAccessCases: graphql(`
    query GrantAccessCasesQuery($motherId: UUID!, $memberId: UUID!) {
      grantAccessCases(motherId: $motherId, memberId: $memberId) {
        id
        encryptionKey
        ... on CompanyReport {
          category {
            id
            name
          }
        }
        organizationalUnit {
          id
          organizationalUnitName
        }
      }
      member(motherId: $motherId, memberId: $memberId) {
        id
        name
        keys {
          id
          publicKey
        }
      }
    }
  `),
}

const mutations = {
  GrantCompanyReportAccess: graphql(`
    mutation GrantCompanyReportAccessMutation($input: GrantCompanyReportAccessInput!) {
      grantCompanyReportAccess(input: $input) {
        success
      }
    }
  `),
}

type GrantAccessActionModalProps = {
  open: boolean
  close: () => void
  institutionId: string
  userId: string
}

const _GrantAccessActionModal = ({ close, institutionId, userId }: GrantAccessActionModalProps) => {
  const navigate = useNavigate()
  const handleError = useCryptoErrorHandler()
  const message = useMessage()

  const { data } = useSuspenseQuery(query.GrantAccessCases, {
    variables: { motherId: institutionId, memberId: userId },
  })

  const recipient = data.member
  const cases = data.grantAccessCases

  const onGrantAccessError = (error: ApolloError) => {
    const errorMessage = error.graphQLErrors?.[0]?.message ?? ''
    if (errorMessage === 'Report access was already given.') {
      message.success('Access already given')
      resetModal()
      close()
    }
  }

  const [grantCompanyReportAccess, { loading }] = useMutation(mutations.GrantCompanyReportAccess, {
    onCompleted: ({ grantCompanyReportAccess }) => {
      const success = grantCompanyReportAccess?.success ?? false
      if (success) {
        message.success(`Access granted to ${recipient?.name ?? 'unknown'}`)
        resetModal()
        close()
      }
    },
    onError: onGrantAccessError,
  })

  const resetModal = () => {
    navigate(routes => routes.home())
  }

  const categories =
    cases.flatMap(aCase => (aCase.__typename === 'CompanyReport' ? [aCase.category.name] : [])) ??
    []
  const companies = cases.map(aCase => aCase?.organizationalUnit?.organizationalUnitName) ?? []

  return (
    <ModalConfirm
      open
      onConfirm={async () => {
        const recipientKeys = await Promise.all(
          cases.map(async aCase => {
            const reportKey = await repackReportKey(
              aCase?.encryptionKey ?? '',
              recipient?.keys?.publicKey ?? ''
            )

            if (reportKey.isErr()) {
              handleError(reportKey.error.message, {
                extra: {
                  caseId: aCase?.id,
                  recipientKeysId: recipient?.keys?.id,
                  recipientId: recipient?.id,
                  hasEncryptionKey: Boolean(aCase?.encryptionKey),
                  hasRecipientPublicKey: Boolean(recipient?.keys?.publicKey),
                },
              })
              return {
                caseId: aCase?.id ?? '',
                value: '',
              }
            }

            return {
              caseId: aCase?.id ?? '',
              value: reportKey.value ?? '',
            }
          })
        )

        if (recipientKeys.some(key => !key.value)) {
          return
        }

        await grantCompanyReportAccess({
          variables: {
            input: {
              motherId: institutionId,
              memberId: userId,
              recipientKeys,
            },
          },
        })
      }}
      confirmState={{ loading }}
      onCancel={() => {
        resetModal()
        close()
      }}
      title={`Grant access to ${recipient?.name ?? ''}`}
    >
      Request for <b>{recipient?.name ?? ''}</b>, <b>{categories.join(', ')}</b>, in{' '}
      <b>{companies.join(', ')}</b>
    </ModalConfirm>
  )
}

const InnerGrantAccessActionModal = withModalSuspense(_GrantAccessActionModal)

type Props = {
  visible: boolean
  close: () => void
  institutionId: string
}

const GrantAccessActionModal = ({ visible, close, institutionId }: Props) => {
  const [params] = useSearchParams()
  const userId = params.get('userId') ?? ''
  return (
    <InnerGrantAccessActionModal
      open={visible && Boolean(userId)}
      close={close}
      institutionId={institutionId}
      userId={userId}
    />
  )
}

export default GrantAccessActionModal
